import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { FC } from 'react';

import ArrowDown from '@/assets/svgs/dropdown/down-arrow.icon.svg?react';
import StyledFormControl from '@/shared/components/core-ui/form/styled-components/styled-form-control';
import { StyledIconButton } from '@/shared/components/group-ui/form-controls/select/styled-select-componenets/styled-select-icon-btn';
import { ChangeEvent } from '@/shared/types/shared-types/change-event.type';
import { SelectOptionsProps } from '@/shared/types/shared-types/select.type';

import SelectImage from '../styled-select-componenets/styled-select-image';
import StyledSelectSubLabel from '../styled-select-componenets/styled-sub-label';

interface MultiSelectProps {
  data: {
    name: string;
    label: string;
    placeholder: string;
    value: SelectOptionsProps[];
    error: any;
    selectOptions: SelectOptionsProps[];
    noResultsFoundText: string;
  };
  config?: {
    isDisabled?: boolean;
  };
  eventHandlers: {
    handleInputChange: (e: ChangeEvent<SelectOptionsProps[]>) => void;
  };
}

const FormMultiSelect: FC<MultiSelectProps> = ({
  data,
  eventHandlers,
  config = { isDisabled: false }
}) => {
  const { name, label, placeholder, value, error = null, selectOptions, noResultsFoundText } = data;
  const { isDisabled } = config;
  const { handleInputChange } = eventHandlers;

  const theme = useTheme();

  const getFieldEndAdornmentIcon = () => {
    return (
      <>
        {value && value.length > 0 && (
          <StyledIconButton
            onClick={(event) => {
              event.stopPropagation();
              handleInputChange({ target: { name, value: [] } });
            }}
          >
            <CloseIcon fontSize="small" />
          </StyledIconButton>
        )}

        <ArrowDown />
      </>
    );
  };

  return (
    <StyledFormControl>
      <FormLabel>{label}</FormLabel>
      <Autocomplete
        multiple
        disabled={isDisabled}
        limitTags={1}
        getLimitTagsText={(num) => `+${num} ${label}`}
        value={value}
        options={selectOptions}
        disableCloseOnSelect={true}
        getOptionLabel={(option) => (option.label ? option.label : '')}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={noResultsFoundText}
        onChange={(event, newValue, reason) => {
          if (
            event.type === 'keydown' &&
            (event as React.KeyboardEvent).key === 'Backspace' &&
            reason === 'removeOption'
          ) {
            return;
          }
          handleInputChange({ target: { name, value: newValue } });
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {option?.imageUrl && <SelectImage src={option?.imageUrl ?? ''} />}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {option.label}
                  <StyledSelectSubLabel>{option.subLabel?.join(',')}</StyledSelectSubLabel>
                </Box>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            placeholder={value && value.length > 0 ? '' : placeholder}
            label=""
            fullWidth
            error={Boolean(error)}
            autoComplete="off"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Box sx={{ maxHeight: '80px', overflowY: 'auto' }}>
                  {params.InputProps.startAdornment}
                </Box>
              ),
              endAdornment: getFieldEndAdornmentIcon()
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: theme.palette.grey[400],
                p: '8px 8px !important'
              },
              '& .MuiOutlinedInput-root': {
                pr: '12px !important',
                border: error && `1px solid ${theme.palette.error.main}`
              }
            }}
          />
        )}
      />

      <FormHelperText error>{error ? error : ' '}</FormHelperText>
    </StyledFormControl>
  );
};

export default FormMultiSelect;
